import { CSSProperties } from 'react';
import {
	createTheme,
	SimplePaletteColorOptions,
	ThemeOptions,
} from '@mui/material/styles';

import { driverTypography, passengerTypography } from './fontUtils';

type CustomPaletteColorOptions = {
	50: string;
	100: string;
	200: string;
	300: string;
	400: string;
	500: string;
	600: string;
	700: string;
	800: string;
	900: string;
} & SimplePaletteColorOptions;

type CustomNeutralPalette = {
	0: string;
	20: string;
	40: string;
	60: string;
	80: string;
	100: string;
	main: string;
	50: string;
	200: string;
	300: string;
	400: string;
	500: string;
	600: string;
	700: string;
	800: string;
	900: string;
};

type AdditionalPalette = {
	driverYellow: string;
};

declare module '@mui/material/Button' {
	interface ButtonPropsVariantOverrides {
		contained: true;
		outlined: true;
		text: true;
		pseudolink: true;
	}

	interface ButtonPropsColorOverrides {
		primaryRevert: true;
		warningRevert: true;
		inherit: false;
		primaryButtonV2: true;
		button_black: true;
		ticketing: true;
		myway: true;
	}
}

declare module '@mui/material/IconButton' {
	interface IconButtonPropsColorOverrides {
		'neutral.0': true;
		'neutral.20': true;
		'neutral.40': true;
		'neutral.60': true;
		'neutral.80': true;
		'neutral.100': true;
	}
}

declare module '@mui/material/SvgIcon' {
	interface SvgIconPropsSizeOverrides {
		lg: true;
		md: true;
		sm: true;
		xs: true;
	}
}

declare module '@mui/material/styles' {
	interface BreakpointOverrides {
		xs: false; // removes the initial breakpoints
		sm: false;
		md: false;
		lg: false;
		xl: false;
		mobile: true;
		tabletP: true; //Portrait
		tabletL: true; //Landscape
		desktop: true;
	}

	export interface PaletteColor {
		light: string;
		main: string;
		dark: string;
		contrastText: string;
		50?: string;
		100?: string;
		200?: string;
		300?: string;
		400?: string;
		500?: string;
		600?: string;
		700?: string;
		800?: string;
		900?: string;
	}

	export interface SimplePaletteColorOptions {
		light?: string;
		main: string;
		dark?: string;
		contrastText?: string;
		50?: string;
		100?: string;
		200?: string;
		300?: string;
		400?: string;
		500?: string;
		600?: string;
		700?: string;
		800?: string;
		900?: string;
	}

	interface Palette {
		neutral: CustomNeutralPalette;
		primaryRevert: SimplePaletteColorOptions;
		primaryButtonV2: SimplePaletteColorOptions;
		button_black: SimplePaletteColorOptions;
		warningRevert: SimplePaletteColorOptions;
		additional: AdditionalPalette;

		bewegt?: CustomPaletteColorOptions;
		myway?: CustomPaletteColorOptions;
		ticketing?: CustomPaletteColorOptions;
		solutions?: CustomPaletteColorOptions;
		logistics?: CustomPaletteColorOptions;
	}

	export interface PaletteOptions {
		neutral?: CustomNeutralPalette;
		primaryRevert: SimplePaletteColorOptions;
		additional?: AdditionalPalette;

		bewegt?: CustomPaletteColorOptions;
		myway?: CustomPaletteColorOptions;
		ticketing?: CustomPaletteColorOptions;
		solutions?: CustomPaletteColorOptions;
		logistics?: CustomPaletteColorOptions;
	}

	interface TypographyVariants {
		desktopHero: CSSProperties;
		desktopTitle: CSSProperties;
		title: CSSProperties;
		subtitle: CSSProperties;
		bodyTitle: CSSProperties;
		'h1-sb'?: CSSProperties;
		'h2-sb'?: CSSProperties;
		h2?: CSSProperties;
		'h3-sb'?: CSSProperties;
		h3: CSSProperties;
		'h4-sb'?: CSSProperties;
		'h5-sb'?: CSSProperties;
		'b1-m'?: CSSProperties;
		'b1-sb'?: CSSProperties;
		b1?: CSSProperties;
		'b2-m'?: CSSProperties;
		b2?: CSSProperties;
		'bu1-m'?: CSSProperties;
		l1?: CSSProperties;
		'l1-m'?: CSSProperties;
		l2?: CSSProperties;
	}

	interface TypographyVariantsOptions {
		desktopHero?: CSSProperties;
		desktopTitle?: CSSProperties;
		title?: CSSProperties;
		subtitle?: CSSProperties;
		bodyTitle?: CSSProperties;
		bodyTitleSmall?: CSSProperties;
		'h1-sb'?: CSSProperties;
		'h2-sb'?: CSSProperties;
		h2?: CSSProperties;
		'h3-sb'?: CSSProperties;
		h3?: CSSProperties;
		'h4-sb'?: CSSProperties;
		'h5-sb'?: CSSProperties;
		'b1-m'?: CSSProperties;
		'b1-sb'?: CSSProperties;
		b1?: CSSProperties;
		'b2-m'?: CSSProperties;
		b2?: CSSProperties;
		'bu1-m'?: CSSProperties;
		l1?: CSSProperties;
		'l1-m'?: CSSProperties;
		l2?: CSSProperties;
	}
}

declare module '@mui/material/Typography' {
	interface TypographyPropsVariantOverrides {
		h4: false;
		h5: false;
		h6: false;
		desktopHero: true;
		desktopTitle: true;
		title: true;
		subtitle: true;
		bodyTitle: true;
		bodyTitleSmall: true;
		'h1-sb': true;
		'h2-sb': true;
		h2: true;
		'h3-sb': true;
		h3: true;
		'h4-sb': true;
		'h5-sb': true;
		'b1-m': true;
		'b1-sb': true;
		b1: true;
		'b2-m': true;
		b2: true;
		'bu1-m': true;
		l1: true;
		'l1-m': true;
		l2: true;
	}
}

const __baseTheme = createTheme();

const moplaPalette = {
	primary: {
		main: '#1C308B',
		dark: '#101D56',
		light: '#DCE3F9',
		contrastText: '#fff',
		50: '#EFF1FA',
		100: '#CED5F3',
		200: '#ABB7ED',
		300: '#8798E9',
		400: '#627AE4',
		500: '#3E5BE0',
		600: '#2343D2',
		700: '#1F39AD',
		800: '#1C2F87',
		900: '#192662',
	},
	secondary: {
		main: '#6FDB89',
		dark: '#208337',
		light: '#C2F0CD',
		contrastText: '#101D56', //TODO check if this is true
	},
	error: {
		main: '#DA1E28',
		dark: '#CA1C25',
		light: '#E95E65',
		contrastText: '#fff',
	},
	warning: {
		main: '#F16E00',
		dark: '#D66200',
		light: '#FDDDC2',
		contrastText: '#fff',
	},
	neutral: {
		0: '#FFFFFF',
		20: '#FAFAFA',
		40: '#EBEBEB',
		60: '#B8B8B8',
		80: '#666666',
		100: '#000000',
		main: '#FFFFFF',
		50: '#E5E5E5',
		// 100: '#CCCCCC', Note: conflict with v2
		200: '#B2B2B2',
		300: '#999999',
		400: '#7F7F7F',
		500: '#656565',
		600: '#4C4C4C',
		700: '#333333',
		800: '#191919',
		900: '#000000',
	},
	text: {
		primary: '#030511',
	},
	primaryRevert: {
		main: '#fff',
		dark: '#FAFAFA',
		light: '#fff',
		contrastText: '#1C308B',
	},
	warningRevert: {
		main: '#fff',
		dark: '#FAFAFA',
		light: '#fff',
		contrastText: '#DA1E28',
	},
	additional: {
		driverYellow: '#FFE66D',
	},
	primaryButtonV2: __baseTheme.palette.augmentColor({
		color: {
			main: '#0E1C5B',
			contrastText: '#fff',
		},
	}),
	button_black: __baseTheme.palette.augmentColor({
		color: {
			main: '#000',
			contrastText: '#000',
		},
	}),
};

const moplaPaletteV2 = {
	primary: {
		main: '#0E1C5B',
		dark: '#172877',
		light: '#ABB7ED',
		contrastText: '#FFFFFF',
		50: '#D1D4E4',
		100: '#CED5F3',
		200: '#ABB7ED',
		300: '#7783B9',
		400: '#6E779D',
		500: '#3E5BE0',
		600: '#2E4094',
		700: '#1C308B',
		800: '#172877',
		900: '#192662',
	},
	secondary: {
		main: '#6FDB89',
		dark: '#267E3B',
		light: '#B1E7BE',
		contrastText: '#FFFFFF',
	},
	bewegt: {
		main: '#6FDB89',
		dark: '#267E3B',
		light: '#B1E7BE',
		contrastText: '#FFFFFF',
		50: '#F0F9F2',
		100: '#D1F0D9',
		200: '#B1E7BE',
		300: '#90E0A3',
		400: '#6ED888',
		500: '#4DD16D',
		600: '#33C255',
		700: '#2CA048',
		800: '#267E3B',
		900: '#1F5B2E',
	},
	myway: {
		main: '#608DF0',
		dark: '#153B8E',
		light: '#A7BEF1',
		contrastText: '#FFFFFF',
		50: '#EEF2FB',
		100: '#CCD9F5',
		200: '#A7BEF1',
		300: '#80A3EF',
		400: '#5A88ED',
		500: '#346DEA',
		600: '#1855DD',
		700: '#1648B6',
		800: '#153B8E',
		900: '#142E66',
	},
	ticketing: {
		main: '#9963AB',
		dark: '#5D3C68',
		light: '#D3BEDA',
		contrastText: '#FFFFFF',
		50: '#F6F3F7',
		100: '#E4D9E8',
		200: '#D3BEDA',
		300: '#C2A4CC',
		400: '#B189BE',
		500: '#9F6EB0',
		600: '#8C579E',
		700: '#754983',
		800: '#5D3C68',
		900: '#452E4C',
	},
	solutions: {
		main: '#8D99AE',
		dark: '#454E5E',
		light: '#F4F4F6',
		contrastText: '#0E1C5B',
		50: '#F4F4F6',
		100: '#DCDFE5',
		200: '#C4CAD4',
		300: '#ACB5C3',
		400: '#949FB2',
		500: '#7C8AA1',
		600: '#66758F',
		700: '#566276',
		800: '#454E5E',
		900: '#353B46',
	},
	logistics: {
		main: '#E48028',
		dark: '#8A4E19',
		light: '#EFCAA9',
		contrastText: '#FFFFFF',
		50: '#FBF4EF',
		100: '#F4DFCD',
		200: '#EFCAA9',
		300: '#EBB484',
		400: '#E89F5E',
		500: '#E48939',
		600: '#D7741E',
		700: '#B1611B',
		800: '#8A4E19',
		900: '#643B17',
	},
	success: {
		main: '#6FDB89',
		dark: '#1F5B2E',
		light: '#B1E7BE',
		contrastText: '#FFFFFF',
		200: '#B1E7BE',
		900: '#1F5B2E',
	},
	warning: {
		main: '#F67B14',
		dark: '#C86512',
		light: '#FEF7F1',
		contrastText: '#FFFFFF',
		200: '#FEF7F1',
		900: '#C86512',
	},
	error: {
		main: '#CC4545',
		dark: '#9E1313',
		light: '#FAEDED',
		contrastText: '#FFFFFF',
		200: '#FAEDED',
		900: '#9E1313',
	},
	neutral: {
		0: '#FFFFFF',
		20: '#FAFAFA',
		40: '#EBEBEB',
		60: '#B8B8B8',
		80: '#666666',
		// 100: '#000000', Note: conflict with v1
		main: '#FFFFFF',
		50: '#E5E5E5',
		100: '#CCCCCC',
		200: '#B2B2B2',
		300: '#999999',
		400: '#7F7F7F',
		500: '#656565',
		600: '#4C4C4C',
		700: '#333333',
		800: '#191919',
		900: '#000000',
	},
	text: {
		primary: '#0E1C5B',
	},
	primaryRevert: {
		main: '#FFFFFF',
		dark: '#FAFAFA',
		light: '#FFFFFF',
		contrastText: '#0E1C5B',
	},
};

export const PaletteColors = moplaPalette;

const combinedMoplaPaletteV2 = {
	...moplaPalette,
	...moplaPaletteV2,
};

export const MOPLA_COLORS = [
	'primary.main',
	'primary.dark',
	'primary.light',
	'primary.50',
	'primary.100',
	'primary.200',
	'primary.300',
	'primary.400',
	'primary.500',
	'primary.600',
	'primary.700',
	'primary.800',
	'primary.900',
	'secondary.main',
	'secondary.dark',
	'secondary.light',
	'error.main',
	'error.dark',
	'error.light',
	'warning.main',
	'warning.dark',
	'warning.light',
	'neutral.0',
	'neutral.20',
	'neutral.40',
	'neutral.60',
	'neutral.80',
	'neutral.100',
	'neutral.main',
	'neutral.50',
	'neutral.200',
	'neutral.300',
	'neutral.400',
	'neutral.500',
	'neutral.600',
	'neutral.700',
	'neutral.800',
	'neutral.900',
	'text.primary',
	'primaryRevert.main',
	'primaryRevert.dark',
	'primaryRevert.light',
	'primaryRevert.contrastText',
	'additional.driverYellow',
] as const;

export const MOPLA_COLORS_V2 = [
	...MOPLA_COLORS,
	'bewegt.main',
	'bewegt.50',
	'bewegt.100',
	'bewegt.200',
	'bewegt.300',
	'bewegt.400',
	'bewegt.500',
	'bewegt.600',
	'bewegt.700',
	'bewegt.800',
	'bewegt.900',
	'myway.main',
	'myway.50',
	'myway.100',
	'myway.200',
	'myway.300',
	'myway.400',
	'myway.500',
	'myway.600',
	'myway.700',
	'myway.800',
	'myway.900',
	'ticketing.main',
	'ticketing.50',
	'ticketing.100',
	'ticketing.200',
	'ticketing.300',
	'ticketing.400',
	'ticketing.500',
	'ticketing.600',
	'ticketing.700',
	'ticketing.800',
	'ticketing.900',
	'solutions.main',
	'solutions.50',
	'solutions.100',
	'solutions.200',
	'solutions.300',
	'solutions.400',
	'solutions.500',
	'solutions.600',
	'solutions.700',
	'solutions.800',
	'solutions.900',
	'logistics.main',
	'logistics.50',
	'logistics.100',
	'logistics.200',
	'logistics.300',
	'logistics.400',
	'logistics.500',
	'logistics.600',
	'logistics.700',
	'logistics.800',
	'logistics.900',
	'success.main',
	'success.dark',
	'success.light',
] as const;

export type TMoplaColors = typeof MOPLA_COLORS[number];
export type TMoplaColorsV2 = typeof MOPLA_COLORS_V2[number];

const themeConfig: ThemeOptions = {
	breakpoints: {
		values: {
			mobile: 0,
			tabletP: 768,
			tabletL: 1024,
			desktop: 1440,
		},
	},
	palette: PaletteColors,
	components: {
		MuiIconButton: {
			styleOverrides: {
				root: ({ theme, ownerState: { color } }) => ({
					...(color === 'neutral.0' && { color: theme.palette.neutral[0] }),
					...(color === 'neutral.20' && { color: theme.palette.neutral[20] }),
					...(color === 'neutral.40' && { color: theme.palette.neutral[40] }),
					...(color === 'neutral.60' && { color: theme.palette.neutral[60] }),
					...(color === 'neutral.80' && { color: theme.palette.neutral[80] }),
					...(color === 'neutral.100' && { color: theme.palette.neutral[100] }),
				}),
				sizeLarge: {
					fontSize: '35px',
				},
				sizeMedium: {
					fontSize: '24px',
				},
				sizeSmall: {
					fontSize: '20px',
				},
			},
			defaultProps: {
				color: 'primary',
			},
		},
		MuiSvgIcon: {
			defaultProps: {
				fontSize: 'inherit',
			},
			styleOverrides: {
				fontSizeLarge: {
					fontSize: '35px',
				},
				fontSizeMedium: {
					fontSize: '24px',
				},
				fontSizeSmall: {
					fontSize: '20px',
				},
			},
		},
		MuiRadio: {
			styleOverrides: {
				root: ({ theme }) => ({
					'&, &.Mui-checked': {
						color: theme.palette.primary.main,
					},
				}),
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: ({ theme }) => ({
					'&, &.Mui-checked': {
						color: theme.palette.primary.main,
					},
					'&.Mui-disabled': {
						color: theme.palette.neutral[60],
					},
					'@media (hover: none)': {
						'&:hover': {
							backgroundColor: 'transparent',
						},
					},
				}),
			},
		},
		MuiPaper: {
			defaultProps: {
				elevation: 1,
			},
			styleOverrides: {
				rounded: {
					borderRadius: 10,
				},
				/**
				 * This is not specified in our DS, although can possibly be in the future.
				 * For now, lets use elevation1 by default
				 * */
				elevation1: {
					boxShadow: '4px 4px 6px 0px rgba(0, 0, 0, 0.1)',
				},
				elevation2: {
					boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
				},
			},
		},
		MuiDivider: {
			styleOverrides: {
				root: ({ theme }) => ({
					borderColor: theme.palette.neutral[60],
				}),
				light: ({ theme }) => ({
					borderColor: theme.palette.neutral[40],
				}),
			},
		},
		/**
		 * This is a temporary workaround for a problem caused by FocusTrap mui's internal component
		 * https://github.com/mui/material-ui/issues/17497
		 * Problem happens, when those conditions are met:
		 *  * FullscreenOverlay component is opened
		 *  * MplModal is also opened, but it is mounted outside the FullscreenOverlay children
		 *  * any input is mounted in the MplModal. This input won't be able to focus
		 * The implementation MplModal with mui's Dialog component inside did not resolve the issue.
		 * */
		MuiModal: {
			defaultProps: {
				disableEnforceFocus: true,
			},
		},
		MuiSwipeableDrawer: {
			defaultProps: {
				disableSwipeToOpen: true,
			},
		},
		MuiFormControlLabel: {
			styleOverrides: {
				root: {
					'& .MuiFormControlLabel-label.Mui-disabled': {
						color: 'unset',
					},
				},
			},
			defaultProps: {
				slotProps: {
					typography: {
						variant: 'body2',
					},
				},
			},
		},
		MuiToggleButton: {
			styleOverrides: {
				root: ({ theme }) => ({
					...theme.typography.body1,
					fontWeight: 700,
					textTransform: 'unset',
					border: '2px solid',
					borderRadius: 8,
					padding: '16px 11px',
				}),
			},
			variants: [
				{
					props: { color: 'primary' },
					style: ({ theme }) => ({
						borderColor: theme.palette.primary.main,
						color: theme.palette.primary.main,

						'&.Mui-selected, &.Mui-selected:hover': {
							backgroundColor: theme.palette.primary.main,
							color: theme.palette.neutral[0],
						},
					}),
				},
			],
		},
		MuiButton: {
			styleOverrides: {
				root: ({ theme, ownerState }) => ({
					...(theme.typography?.['bu1-m'] ?? theme.typography.body1),
					borderRadius: 8,
					textTransform: 'none',

					...(ownerState.variant === 'outlined' && {
						'&, &:hover': {
							border: 'none',
							outline: `2px solid`,
							outlineOffset: '-2px',
						},
					}),

					...(ownerState.variant === 'pseudolink' && {
						color: theme.palette[ownerState.color || 'primary']?.main,
					}),

					'&.Mui-disabled': {
						...(ownerState.variant === 'contained' && {
							color: theme.palette.neutral[80],
							backgroundColor: theme.palette.neutral[40],
						}),

						...(ownerState.variant === 'outlined' && {
							color: theme.palette.neutral[80],
							outlineColor: theme.palette.neutral[40],
						}),

						...(ownerState.variant === 'text' && {
							color: theme.palette.neutral[80],
						}),

						...(ownerState.variant === 'pseudolink' && {
							color: theme.palette.neutral[80],
						}),
					},
				}),
				sizeLarge: {
					fontWeight: 700,
					padding: '16px 22px',
				},
				sizeSmall: {
					fontWeight: 400,
					padding: '8px 16px',
				},
			},
			defaultProps: {
				type: 'button',
				variant: 'contained',
				color: 'primary',
				size: 'large',
			},
			variants: [
				{
					props: { variant: 'contained', color: 'secondary' },
					style: ({ theme }) => ({
						color: theme.palette.primary.dark,

						':hover': {
							color: theme.palette.neutral[0],
						},
					}),
				},
				{
					props: { variant: 'pseudolink' },
					style: {
						/* Custom underline since native will not consider icons */
						position: 'relative',
						'&:after': {
							content: '""',
							position: 'absolute',
							bottom: '1px',
							height: '1px',
							width: '100%',
							backgroundColor: 'currentColor',
							transform: 'scaleX(0)',
							transformOrigin: 'center',
							transition: 'transform 0.2s',
						},

						'@media (hover: none)': {
							'&:active:after': {
								transform: 'scaleX(1)',
							},
						},

						'@media (hover: hover)': {
							'&:hover:after': {
								transform: 'scaleX(1)',
							},
						},
					},
				},
				{
					props: { variant: 'pseudolink', size: 'large' },
					style: { padding: '4px 0' },
				},
				{
					props: { variant: 'pseudolink', size: 'small' },
					style: { padding: 0 },
				},
			],
		},
	},
};

export const driverTheme = createTheme({
	...themeConfig,
	typography: driverTypography,
});

const passengerThemeV1 = createTheme({
	...themeConfig,
	typography: passengerTypography,
});

export const passengerThemeV2 = createTheme({
	...themeConfig,
	palette: combinedMoplaPaletteV2,
	components: {
		...themeConfig.components,
		MuiSvgIcon: {
			defaultProps: {
				inheritViewBox: true,
				fontSize: 'inherit',
			},
			styleOverrides: {
				fontSizeLarge: {
					fontSize: '35px',
				},
				fontSizeMedium: {
					fontSize: '24px',
				},
				fontSizeSmall: {
					fontSize: '16px',
				},
				fontSizeLg: {
					fontSize: '35px',
				},
				fontSizeMd: {
					fontSize: '24px',
				},
				fontSizeSm: {
					fontSize: '20px',
				},
				fontSizeXs: {
					fontSize: '16px',
				},
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: ({ theme }) => ({
					'&, &.Mui-checked': {
						color: theme.palette.primary.main,
					},
					'&.Mui-disabled': {
						color: theme.palette?.solutions?.[100],
					},
					'&:hover': {
						backgroundColor: theme.palette?.solutions?.[100],
					},
					'&.Mui-focusVisible, &:active': {
						backgroundColor: theme.palette.primary[200],
					},
					'@media (hover: none)': {
						'&:hover': {
							backgroundColor: 'transparent',
						},
					},
				}),
			},
		},
		MuiButton: {
			styleOverrides: {
				root: ({ theme, ownerState }) => ({
					...(theme.typography?.['bu1-m'] ?? theme.typography.body1),
					borderRadius: 8,
					textTransform: 'none',

					...(ownerState.variant === 'contained' && {
						'&, &:hover, &:active': {
							boxShadow: 'none',
						},
					}),

					...(ownerState.variant === 'outlined' && {
						'&, &:hover': {
							border: 'none',
							outline: `1px solid`,
							outlineOffset: '-1px',
						},
					}),

					...(ownerState.variant === 'pseudolink' && {
						color: theme.palette[ownerState.color || 'primary']?.main,
					}),

					'&.Mui-disabled': {
						...(ownerState.variant === 'contained' && {
							color: theme.palette.solutions?.[800],
							backgroundColor: theme.palette.solutions?.[100],
						}),

						...(ownerState.variant === 'outlined' && {
							color: theme.palette.solutions?.[800],
							outlineColor: theme.palette.solutions?.[100],
						}),

						...(ownerState.variant === 'text' && {
							color: theme.palette.solutions?.[800],
						}),

						...(ownerState.variant === 'pseudolink' && {
							color: theme.palette.solutions?.[800],
						}),
					},

					'& .MuiButton-startIcon': {
						marginRight: '4px',
						marginLeft: '-2px',
					},
				}),
				sizeLarge: {
					height: 48,
					padding: '0 24px',
				},
				sizeSmall: {
					padding: '8px 16px',
				},
				startIcon: {
					'& .MuiSvgIcon-root': {
						fontSize: '24px',
					},
				},
			},
			defaultProps: {
				type: 'button',
				variant: 'contained',
				color: 'primary',
				size: 'large',
			},
			variants: [
				{
					props: { variant: 'contained', color: 'secondary' },
					style: ({ theme }) => ({
						color: theme.palette.primary.dark,

						':hover': {
							color: theme.palette.neutral[0],
						},
					}),
				},
				{
					props: { variant: 'pseudolink' },
					style: {
						/* Custom underline since native will not consider icons */
						position: 'relative',
						'&:after': {
							content: '""',
							position: 'absolute',
							bottom: '1px',
							height: '1px',
							width: '100%',
							backgroundColor: 'currentColor',
							transform: 'scaleX(0)',
							transformOrigin: 'center',
							transition: 'transform 0.2s',
						},

						'@media (hover: none)': {
							'&:active:after': {
								transform: 'scaleX(1)',
							},
						},

						'@media (hover: hover)': {
							'&:hover:after': {
								transform: 'scaleX(1)',
							},
						},
					},
				},
				{
					props: { variant: 'pseudolink', size: 'large' },
					style: { padding: '4px 0' },
				},
				{
					props: { variant: 'pseudolink', size: 'small' },
					style: { padding: 0 },
				},
			],
		},
		MuiFormControlLabel: {
			styleOverrides: {
				root: {
					marginLeft: 'unset',
					'& .MuiFormControlLabel-label.Mui-disabled': {
						color: 'unset',
					},
				},
				label: {
					color: '#0E1C5B',
				},
			},
			defaultProps: {
				slotProps: {
					typography: {
						variant: 'body2',
					},
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: ({ theme }) => ({
					height: '48px',
					borderRadius: '8px',
					paddingRight: '12px',

					'& fieldset': {
						border: `1px solid ${theme.palette?.solutions?.[500]}`,
					},
					'& .MuiOutlinedInput-notchedOutline': {
						borderWidth: 1,
					},

					'&.Mui-focused:not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
						{
							borderWidth: 1,
							borderColor: theme.palette.primary.main,
						},

					'@media (hover: hover)': {
						'&:hover:not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
							{
								borderColor: theme.palette.primary.main,
								borderWidth: 1,
							},
					},

					'&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': {
						borderWidth: 1,
					},

					// Note: iOS 26 Native and Safari specific-fix
					// Fix for floating label border gap issue
					'@supports (-webkit-appearance: none)': {
						'& .MuiOutlinedInput-notchedOutline legend': {
							visibility: 'visible !important',
						},
					},
				}),

				input: ({ theme }) => ({
					padding: '16px 0 16px 12px',
					fontSize: '14px',
					fontWeight: 400,
					color: theme.palette.primary.main,
					caretColor: theme.palette.primary.main,

					'&[type=number]::-webkit-outer-spin-button, &[type=number]::-webkit-inner-spin-button':
						{
							WebkitAppearance: 'none',
							margin: 0,
						},
					'&[type=number]': {
						MozAppearance: 'textfield',
					},
				}),

				notchedOutline: {
					legend: {
						fontSize: '0.64em',
					},
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: ({ theme }) => ({
					...theme.typography.b2,
					lineHeight: 'normal',
					padding: 0,
					color: theme.palette?.solutions?.[600],

					'&.Mui-focused:not(.Mui-error)': {
						color: theme.palette?.solutions?.[500],
					},
					'&.MuiInputLabel-shrink': {
						...theme.typography.l1,
						lineHeight: 'normal',
						transform: 'translate(14px, -6px) scale(0.85)',
					},

					'&.Mui-error': {
						borderWidth: 1,
						color: theme.palette.error.main,
					},
					'&.Mui-error.Mui-focused': {
						color: theme.palette.error.main,
					},
				}),
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: ({ theme }) => ({
					...theme.typography.b2,
					marginTop: '8px',
					marginLeft: 0,

					'&.Mui-error': {
						color: theme.palette?.error?.main,
					},
				}),
			},
		},
		MuiInputBase: {
			styleOverrides: {
				input: ({ theme }) => ({
					'&:-webkit-autofill': {
						borderRadius: 'inherit',
						height: '21px',
						WebkitTextFillColor: `${theme.palette.text.primary} !important`,
						boxShadow: `0 0 0px 40rem ${theme.palette.neutral[0]} inset !important`,
					},
				}),
			},
		},
		MuiBottomNavigationAction: {
			styleOverrides: {
				fontFamily: 'Poppins, sans-serif',
				root: ({ theme }) => ({
					fontSize: '22px',
					'& .MuiBottomNavigationAction-label': {
						// TODO: use proper Typography
						fontFamily: 'Poppins, sans-serif',
						fontSize: '10px',
						lineHeight: '15px',
						color: theme.palette?.solutions?.main,
					},

					'& .MuiBottomNavigationAction-label.Mui-selected': {
						// TODO: use proper Typography
						fontFamily: 'Poppins, sans-serif',
						fontSize: '10px',
						lineHeight: '15px',
						color: theme.palette.primary.main,
					},
				}),
			},
		},
	},
	typography: passengerTypography,
} as ThemeOptions);

export type TTheme = typeof passengerThemeV1;

/** @deprecated Use `passengerThemeV1`. Will be removed after the V2 rollout. */
export { passengerThemeV1 as theme };
